import { useCallback, useEffect, useMemo, useState } from 'react';
import { ADS_CHAPTER_TARGET } from '@/lib/journeyTargets';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAdsAccounts, type AdsAccount } from '@/hooks/useAdsPerformance';
import { LINKED_ADS_ACCOUNT_QUERY_KEY } from '@/hooks/useLinkedAdsAccount';
import { ADS_FEED_SUMMARY_QUERY_KEY } from '@/hooks/useAdsFeedSummary';
import { trackConversion } from '@/lib/analytics';
import { adsErrorKey } from '@/lib/adsConnectErrors';
import { parseSuggestedCustomerIds } from '@/lib/adsPickerList';
import i18n from '@/lib/i18n';

export type AdsConnectSource = 'feed_check' | 'settings';

/**
 * Was die Instanz tun darf, die den Vollbild-Rueckweg (`?adsOauth=...`)
 * verbraucht.
 *
 * `'full'` ist der Feed-Check: dort gibt es genau EINE Instanz, und sie kennt
 * das Merchant Center, um das es geht -- sie darf deshalb den ganzen Ablauf
 * fahren (Zuordnung pruefen, Dialog oder Erst-Abruf).
 *
 * `'notify-only'` sind die Einstellungen: dort haengt eine Instanz an JEDER
 * Google-Konto-Karte. Welche den Rueckweg verbraucht, entscheidet die
 * Render-Reihenfolge und nicht der Consent, und die verbrauchende Karte haengt
 * an IHREM ersten Merchant Center, das mit dem Consent nichts zu tun haben
 * muss. Eine Instanz in diesem Modus meldet deshalb nur, dass Google
 * zurueckgekommen ist, und laesst die Zuordnung dem Nutzer: er klickt "Konto
 * zuordnen" auf der richtigen Karte. Kein Dialog, kein Erst-Abruf, kein
 * automatisches Verknuepfen.
 */
export type AdsUrlReturnMode = 'full' | 'notify-only';

export interface AdsPickerCandidate {
  id: string;
  adsCustomerId: string;
  name: string | null;
  currency: string | null;
  selectable: boolean;
  linkedElsewhere: boolean;
  /**
   * Google kennt an DIESER Merchant Center eine Verknuepfung zu diesem Konto
   * (`ads-link-suggest`). Ein Hinweis, keine Zusicherung: die Auskunft ist
   * Googles, und die Zuordnung bleibt eine Entscheidung des Nutzers.
   *
   * Solange der Vorschlag laeuft oder scheitert, ist das Feld fuer jede Zeile
   * `false` -- der Dialog steht dann ohne Vorauswahl da, und das ist ein
   * vollstaendig bedienbarer Zustand.
   */
  suggested: boolean;
}

export interface AdsConnectFlow {
  onConnected: () => void;
  openPicker: () => void;
  pickerOpen: boolean;
  closePicker: () => void;
  candidates: AdsPickerCandidate[];
  confirmLink: (adsAccountId: string) => Promise<boolean>;
  isLinking: boolean;
  pollUntil: number | null;
  /**
   * Ein Erst-Abruf laeuft gerade (Final-Review F3).
   *
   * Die Flaechen lesen das als `disabled` fuer den Verbinden-Knopf, und
   * `openPicker` ist in diesem Zustand ein No-op. Grund: ein Erst-Abruf stoesst
   * einen vollen 30-Tage-Pull ueber alle aktiven Konten des Grants an
   * (Gerstaecker rund 30 Kettenglieder), und das Operationskontingent des
   * Developer-Tokens teilen sich ALLE Kunden. Die Function beantwortet einen
   * zweiten Aufruf inzwischen mit `started:false`; der Knopf soll ihn gar nicht
   * erst ausloesen.
   */
  busy: boolean;
}

/** Wie lange das Kapitel nach dem Connect auf den ersten Eintrag pollt (Spec 4.3 Punkt 3). */
export const FIRST_PULL_POLL_WINDOW_MS = 3 * 60 * 1000;

/**
 * Das kurze Fenster fuer den Modus `cache-only` (Re-Review N2).
 *
 * Dort hat `ads-first-pull` die Verdichtung INNERHALB des Requests gerechnet:
 * wenn die Antwort da ist, steht die Zeile. Gepollt wird trotzdem, weil der
 * Eintrag ueber PostgREST kommt und der Cache des Clients ihn erst holen muss
 * -- aber drei Minuten waeren dafuer eine Sperre ohne Anlass, `busy` haengt am
 * selben Fenster.
 */
export const CACHE_ONLY_POLL_WINDOW_MS = 30 * 1000;

/**
 * Wie lange nach DIESER Antwort von `ads-first-pull` gepollt wird.
 *
 * Rein und exportiert, damit die drei Formen an einer Stelle stehen und nicht
 * in einem Effekt verteilt (die Formen selbst stehen im Modulkopf von
 * `supabase/functions/ads-first-pull/first-pull.ts`):
 *
 * - `{started:true, mode:'cache-only'}` -- fertig gerechnet, kurzes Fenster.
 * - `{started:false, reason:'pull-running'}` -- eine Kette laeuft und schreibt
 *   den Cache; weiterpollen wie beim eigenen Pull.
 * - alles andere (202 mit `ads_account_id`, unbekannte oder unlesbare Antwort)
 *   -- volles Fenster. Fail-lang statt fail-kurz: ein zu kurzes Fenster laesst
 *   den Kunden vor einem leeren Kapitel sitzen, ein zu langes kostet ein paar
 *   Abfragen.
 */
export function resolveFirstPullPollWindow(data: unknown): number {
  const mode = (data as { mode?: unknown } | null)?.mode;
  return mode === 'cache-only' ? CACHE_ONLY_POLL_WINDOW_MS : FIRST_PULL_POLL_WINDOW_MS;
}

/**
 * Der zuletzt VERBRAUCHTE Vollbild-Rueckweg, modulweit statt je Hook-Instanz.
 *
 * Die Sperre lag zuerst in einem `useRef`, und das ist genau so lange richtig,
 * wie es hoechstens eine Instanz je Seite gibt. In den Einstellungen gibt es
 * eine JE GOOGLE-KONTO-KARTE (Task 13): alle N Effekte laufen im selben Commit
 * und lesen denselben, noch nicht bereinigten `searchParams`. Mit einer
 * Instanz-Sperre haette ein einziger `adsOauth=success`-Rueckweg N
 * Konversionsmeldungen, bis zu N `ads-first-pull`-Aufrufe und bis zu N offene
 * Dialoge erzeugt -- auch auf Karten, deren Google-Konto am Consent gar nicht
 * beteiligt war.
 *
 * Geschluesselt wird auf den VERBRAUCHTEN WERT (`adsOauth` plus `code`) und
 * nicht auf ein blosses Boolean: eine Sperre, die nur "schon mal passiert"
 * merkt, verschluckt den zweiten echten Rueckweg derselben Sitzung. Aus
 * demselben Grund faellt sie zurueck, sobald der Parameter aus der URL
 * verschwunden ist (siehe Effekt unten) -- zwei aufeinanderfolgende Erfolge
 * tragen sonst dieselbe Kennung `success|` und der zweite bliebe stumm.
 */
let consumedAdsOauth: string | null = null;

/**
 * NUR FUER TESTS: die modulweite Sperre zuruecksetzen.
 *
 * Modulzustand ueberlebt `vi.clearAllMocks()` und das Unmounten; ohne dieses
 * Ventil truege der zweite Test die Entscheidung des ersten und waere gruen,
 * ohne etwas zu pruefen.
 */
export function __resetAdsConnectUrlLockForTests(): void {
  consumedAdsOauth = null;
}

/**
 * Meldung, wenn die ZUORDNUNG nicht durchgegangen ist.
 *
 * Der Titel bleibt der des Connect-Fehlers, der Rumpf ist seit dem 10.09.2026
 * ein eigener Schluessel (`feedCheck.ads.picker.linkFailed`). Grund: Verbinden
 * und Zuordnen sind zwei verschiedene Vorgaenge. Der generische Connect-Text
 * spricht vom Verbinden, und genau das ist hier bereits passiert -- der Nutzer
 * steht vor dem Auswahldialog seines VERBUNDENEN Kontos. Die RPC nennt keinen
 * Grund (kein Existenz-Orakel ueber fremde Konten), also nennt die Meldung
 * auch keinen; sie sagt nur, dass es nicht geklappt hat und man es noch einmal
 * versuchen kann.
 *
 * Beide Fehlpfade von `confirmLink` (die RPC antwortet `false`, und die RPC
 * wirft) laufen absichtlich hier durch: fuer DENSELBEN Vorgang soll es EIN
 * Fehlerbild geben.
 */
function notifyLinkFailed(): void {
  toast.error(i18n.t('adsPerformance:connect.errorTitle'), {
    description: i18n.t('optimization:feedCheck.ads.picker.linkFailed'),
  });
}

/**
 * Der eine Connect-Ablauf fuer Feed-Check und Settings (Spec 4.3). Zwei Ausloeser:
 * die Popup-Nachricht (ueber `onConnected` des Knopfs) und der URL-Parameter
 * `adsOauth` nach einem Vollbild-Consent (Parameter danach weg, Muster
 * AdsPerformance.tsx).
 *
 * MEHRERE INSTANZEN JE SEITE SIND ERLAUBT (Einstellungen: eine je
 * Google-Konto-Karte). Den URL-Rueckweg verarbeitet genau EINE davon, naemlich
 * die, deren Effekt zuerst laeuft -- durchgesetzt von der modulweiten Sperre
 * `consumedAdsOauth` oben, nicht von einem Instanz-Ref. Genau deshalb gibt es
 * `urlReturnMode`: wer den Rueckweg verbraucht, ist bei mehreren Instanzen
 * nicht die Karte, die den Consent ausgeloest hat, und darf dann nur melden.
 *
 * OHNE FEED-KONTEXT (Einstellungen, `feedId === null`) ist `startFirstPull` ein
 * No-op und `pollUntil` bleibt `null`: es gibt dort keinen Feed, fuer den ein
 * Erst-Abruf laufen koennte. Die Kontozeile steht danach auf "verbunden, noch
 * keine Zahlen", bis der naechtliche Lauf fuellt -- das ist der vorgesehene
 * Zustand und kein Fehlschlag (Task 13).
 *
 * Verknuepft wird NIE automatisch und NIE ueber set_active_google_ads_account
 * (deaktiviert Geschwister; live haengen vier Rollout-Konten an einem
 * Google-Konto). Der Weg ist immer: Kandidaten aus der DB, Auswahl,
 * link_google_ads_account fuer (Konto, aktive MC).
 */
export function useAdsConnectFlow(input: {
  source: AdsConnectSource;
  merchantCenterId: string | null;
  googleAccountId: string | null;
  feedId: string | null;
  /** Default `'full'`; die Einstellungen setzen `'notify-only'` (siehe Typ). */
  urlReturnMode?: AdsUrlReturnMode;
}): AdsConnectFlow {
  const queryClient = useQueryClient();
  const accounts = useAdsAccounts();
  const navigate = useNavigate();

  /**
   * Der Weg zu den Zahlen, gleich in der Erfolgs-Meldung (Spec 2026-09-16,
   * Abschnitt 7).
   *
   * Bis dahin endete ein gelungener Connect in einem Toast ohne Ziel -- der
   * Nutzer hatte gerade sein Ads-Konto verbunden und musste selbst finden, wo
   * die Zahlen stehen. Die Aktion fuehrt auf `/#ads`; die Startseite klappt das
   * Kapitel dort auf und scrollt hin.
   */
  const toastAktion = useMemo(() => ({
    label: i18n.t('adsPerformance:connect.toastToChapter'),
    onClick: () => navigate(ADS_CHAPTER_TARGET),
  }), [navigate]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [pollUntil, setPollUntil] = useState<number | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Welches Konto Google an DIESER Merchant Center kennt (Nachtrag 12.09.2026).
   *
   * WARUM ERST BEIM OEFFNEN. `google-oauth-handler/ads-connect.ts` loest die
   * Verknuepfung nur zum Consent-Zeitpunkt auf und schreibt sie nach
   * `google_ads_accounts.linked_merchant_center_id`. Jedes SPAETER angelegte
   * Merchant Center fehlt in dieser Momentaufnahme -- und genau dort steht der
   * Nutzer, wenn er "Konto zuordnen" klickt. Gefragt wird deshalb auf Zuruf;
   * solange niemand zuordnet, gibt es nichts vorzuschlagen, und ein Aufruf je
   * Seitenaufbau waere ein Google-Request fuer nichts.
   *
   * `staleTime` fuenf Minuten: die Antwort beschreibt einen Zustand bei Google,
   * der sich waehrend einer Zuordnung nicht aendert. Ein zweites Oeffnen
   * desselben Dialogs fragt nicht erneut.
   */
  const suggestion = useQuery({
    queryKey: ['ads-link-suggest', input.merchantCenterId],
    enabled: pickerOpen && !!input.merchantCenterId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('ads-link-suggest', {
        body: { merchant_center_id: input.merchantCenterId },
      });
      // KEIN `throw`. Ein Fehler wird einmal gemeldet und danach wie "kein
      // Vorschlag" behandelt: geworfen wuerde React Query es mehrfach
      // wiederholen und jeder Versuch eine weitere Sentry-Meldung erzeugen --
      // fuer eine Vorauswahl, ohne die der Dialog vollstaendig bedienbar ist.
      if (error) {
        Sentry.captureException(error, { tags: { feature: 'ads-chapter', stage: 'link-suggest' } });
        return [] as string[];
      }
      return parseSuggestedCustomerIds(data);
    },
  });

  const suggestedIds = useMemo(() => new Set(suggestion.data ?? []), [suggestion.data]);

  const candidates = useMemo<AdsPickerCandidate[]>(() => {
    const rows = (accounts.data ?? []) as AdsAccount[];
    return rows
      .filter((a) => a.google_account_id === input.googleAccountId)
      .map((a) => {
        const linkedElsewhere = a.linked_merchant_center_id !== null && a.linked_merchant_center_id !== input.merchantCenterId;
        return {
          id: a.id,
          adsCustomerId: a.ads_customer_id,
          name: a.descriptive_name,
          currency: a.currency,
          selectable: !a.is_manager && !linkedElsewhere,
          linkedElsewhere,
          suggested: suggestedIds.has(a.ads_customer_id),
        };
      });
  }, [accounts.data, input.googleAccountId, input.merchantCenterId, suggestedIds]);

  /**
   * Was nach einer Aenderung an der Verknuepfung neu gelesen werden muss.
   *
   * Die Kontoliste (`ads-accounts`) steht hier bewusst NICHT drin: `afterConnect`
   * braucht sie synchron und holt sie deshalb selbst per `refetch()`, das
   * denselben Cache schreibt. Beides zusammen waeren zwei Netzaufrufe fuer
   * dieselbe Antwort. `confirmLink` hat keinen `refetch` und invalidiert die
   * Liste an Ort und Stelle -- dort hat sich `linked_merchant_center_id`
   * tatsaechlich geaendert.
   */
  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [LINKED_ADS_ACCOUNT_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [ADS_FEED_SUMMARY_QUERY_KEY] });
  }, [queryClient]);

  const startFirstPull = useCallback(async () => {
    if (!input.feedId) return;
    // Zuerst das lange Fenster: `busy` soll ab dem Klick stehen, nicht erst
    // nach dem Netzweg. Die Antwort kuerzt es gleich wieder, wo sie darf.
    setPollUntil(Date.now() + FIRST_PULL_POLL_WINDOW_MS);
    const { data, error } = await supabase.functions.invoke('ads-first-pull', { body: { feed_id: input.feedId } });
    // Best-effort: scheitert der Erst-Abruf, fuellt der Nachtlauf. Sentry, kein Toast.
    if (error) {
      Sentry.captureException(error, { tags: { feature: 'ads-chapter', stage: 'first-pull' } });
      return;
    }
    // DIE ANTWORT SAGT, WIE LANGE ES DAUERT (Re-Review N2). Sie nicht zu lesen
    // hiess: nach einem Cache-Refresh, der mit der Antwort fertig war, blieb
    // der Verbinden-Knopf drei Minuten gesperrt.
    setPollUntil(Date.now() + resolveFirstPullPollWindow(data));
  }, [input.feedId]);

  const afterConnect = useCallback(async () => {
    trackConversion('google_ads_connected', { source: input.source });
    invalidate();
    const fresh = await accounts.refetch();
    // OHNE ZIEL wird nicht zugeordnet, und der Dialog bleibt zu: er fragt
    // "welches Konto bewirbt die Produkte dieses Merchant Centers?", und es
    // gibt keines. Der Zweig ist nicht theoretisch -- eine Konto-Karte in den
    // Einstellungen hat ihn, sobald sie null oder mehrere Merchant Center
    // fuehrt. Er muss VOR der Zeile darunter stehen: `linkedHere` verglich
    // sonst `null === null` und meldete jede unverknuepfte Zeile als bereits
    // zugeordnet, der Connect endete vollkommen stumm.
    if (!input.merchantCenterId) {
      toast.success(i18n.t('adsPerformance:connect.successTitle'), {
        description: i18n.t('optimization:feedCheck.ads.settings.connectedToast'),
        action: toastAktion,
      });
      return;
    }
    const rows = ((fresh.data ?? []) as AdsAccount[]).filter((a) => a.google_account_id === input.googleAccountId);
    // AKTIV UND VERKNUEPFT, nicht nur verknuepft. `handleAdsConnect` schreibt
    // `linked_merchant_center_id` auf jede Zeile des Grants, aktiviert aber
    // hoechstens eine; gezogen wird ausschliesslich aktiv (Nachtlauf und
    // `ads-first-pull` filtern beide auf `is_active`). Ohne `a.is_active`
    // liefe der Erst-Abruf auf einer inaktiven Zeile in ein
    // `no-linked-account`, der Dialog bliebe zu, und der Kunde wartete auf
    // Zahlen, die niemand holt. Dieselbe Regel wie in `useLinkedAdsAccount`.
    const linkedHere = rows.some((a) => a.is_active && a.linked_merchant_center_id === input.merchantCenterId);
    if (linkedHere) {
      await startFirstPull();
    } else {
      setPickerOpen(true);
    }
  }, [accounts, input.googleAccountId, input.merchantCenterId, input.source, invalidate, startFirstPull]);

  /**
   * Der Vollbild-Rueckweg im Modus `notify-only`: Meldung, frische Kontoliste,
   * EINE Konversionsmeldung (dasselbe Ereignis wie im vollen Modus, ein
   * Consent ist ein Consent). Sonst nichts.
   *
   * Bewusst ohne `invalidate()`: die beiden dort genannten Familien
   * (`linked-ads-account`, `ads-feed-summary`) beschreiben eine VERKNUEPFUNG,
   * und die hat sich hier nicht geaendert -- verbunden ist nicht zugeordnet.
   * Die Kontoliste dagegen hat eine neue Zeile und wird geholt, damit die
   * richtige Karte sofort "Konto zuordnen" anbietet statt "nicht verbunden".
   */
  const notifyConnected = useCallback(() => {
    toast.success(i18n.t('adsPerformance:connect.successTitle'), {
      description: i18n.t('optimization:feedCheck.ads.settings.connectedToast'),
      action: toastAktion,
    });
    trackConversion('google_ads_connected', { source: input.source });
    void accounts.refetch();
  }, [accounts, input.source, toastAktion]);

  // Zweiter Ausloeser: Rueckkehr aus dem Vollbild-Consent.
  useEffect(() => {
    const outcome = searchParams.get('adsOauth');
    if (!outcome) {
      // Der Parameter ist weg (von uns entfernt oder nie dagewesen): die Sperre
      // faellt, damit ein SPAETERER echter Rueckweg mit derselben Kennung
      // wieder durchkommt.
      consumedAdsOauth = null;
      return;
    }
    const token = `${outcome}|${searchParams.get('code') ?? ''}`;
    if (consumedAdsOauth === token) return;
    // IM VOLLMODUS WARTEN, SOLANGE DAS ZIEL FEHLT.
    //
    // Der Feed-Check reicht `merchantCenter?.id ?? null` herein, und diese
    // Zeile ist beim ERSTEN Rendern null: der `SelectionContext` laedt die
    // Merchant Center asynchron. Der Effekt lief bis zum 11.09.2026 trotzdem
    // sofort, nahm den Zweig ohne Ziel, raeumte die URL-Parameter weg und
    // setzte die Sperre -- der ganze Vollbild-Rueckweg war damit verbraucht,
    // bevor er etwas tun konnte, und der Nutzer sah eine Meldung, die ihn auf
    // eine Konto-Karte in den Einstellungen schickt. Genau diesen Weg gehen
    // Browser mit blockiertem Popup, also nicht den Ausnahmefall.
    //
    // Ohne Ziel wird deshalb NICHTS verbraucht: kein Lock, keine aufgeraeumte
    // URL. Ein spaeterer Render mit aufgeloester MC holt denselben Rueckweg,
    // und die Sperre sorgt dafuer, dass das genau einmal passiert.
    //
    // Zwei Ausnahmen, beide brauchen kein Ziel: `notify-only` meldet ohnehin
    // nur (Einstellungen, siehe `AdsUrlReturnMode`), und ein Fehlschlag nennt
    // kein Konto und kein Merchant Center.
    if (outcome !== 'error' && input.urlReturnMode !== 'notify-only' && !input.merchantCenterId) return;
    consumedAdsOauth = token;
    if (outcome === 'error') {
      // Der Fehlerzweig ist in beiden Modi derselbe: er sagt, dass der
      // Consent gescheitert ist, und betrifft kein bestimmtes Konto.
      toast.error(i18n.t('adsPerformance:connect.errorTitle'), { description: i18n.t(`adsPerformance:${adsErrorKey(searchParams.get('code'))}`) });
    } else if (input.urlReturnMode === 'notify-only') {
      notifyConnected();
    } else {
      void afterConnect();
    }
    const next = new URLSearchParams(searchParams);
    next.delete('adsOauth');
    next.delete('code');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, afterConnect, notifyConnected, input.urlReturnMode, input.merchantCenterId]);

  /**
   * Das Poll-Fenster laeuft VON SELBST ab (Final-Review F3).
   *
   * `busy` wird direkt aus `pollUntil` gelesen, und ohne diesen Wecker bliebe
   * der Verbinden-Knopf nach dem Ende des Fensters dauerhaft gesperrt: die
   * Zeit vergeht, aber nichts rendert neu, und ein Zustand, der sich nur bei
   * fremdem Anlass korrigiert, ist eine Sperre ohne Ende. Der Wecker raeumt
   * zugleich das Polling von `useAdsFeedSummary` ab.
   */
  useEffect(() => {
    if (pollUntil === null) return;
    const rest = pollUntil - Date.now();
    if (rest <= 0) { setPollUntil(null); return; }
    const timer = setTimeout(() => setPollUntil(null), rest);
    return () => clearTimeout(timer);
  }, [pollUntil]);

  /** Siehe `AdsConnectFlow.busy`. */
  const busy = pollUntil !== null;

  const confirmLink = useCallback(async (adsAccountId: string): Promise<boolean> => {
    // KEIN ZIEL. Der Dialog kann trotzdem offen stehen: `afterConnect` hat ihn
    // frueher auch ohne Merchant Center geoeffnet, und die Einstellungen
    // koennen ihn ueber eine Karte ohne (oder mit mehreren) Merchant Centern
    // erreichen. Der Satz "der Knopf ist in diesem Zustand nicht erreichbar"
    // stand hier bis zum 11.09.2026 und war ab Task 13 falsch.
    //
    // Ein stummes `return false` liess den Nutzer dann vor einem Dialog
    // stehen, dessen Bestaetigen-Knopf sichtbar nichts tut. Also: schliessen
    // und dasselbe Fehlerbild wie jeder andere gescheiterte Zuordnungsversuch.
    if (!input.merchantCenterId) {
      setPickerOpen(false);
      notifyLinkFailed();
      return false;
    }
    setIsLinking(true);
    try {
      const { data, error } = await supabase.rpc('link_google_ads_account', {
        p_ads_account_id: adsAccountId,
        p_merchant_center_id: input.merchantCenterId,
      });
      if (error) throw error;
      if (data !== true) {
        // Die RPC unterscheidet fremd/MCC/anderweitig verknuepft/nicht
        // vorhanden bewusst nicht (kein Existenz-Orakel). Ein stilles `false`
        // liesse den Nutzer vor einem Dialog stehen, der auf seinen Klick nicht
        // reagiert -- der Dialog bleibt offen, damit er ein anderes Konto
        // waehlen kann, und nichts wird invalidiert.
        notifyLinkFailed();
        return false;
      }
      invalidate();
      // Nur hier: die Kontoliste selbst hat sich geaendert
      // (`linked_merchant_center_id`), und es gibt an dieser Stelle kein
      // `refetch`, das denselben Cache ohnehin schreiben wuerde.
      queryClient.invalidateQueries({ queryKey: ['ads-accounts'] });
      setPickerOpen(false);
      await startFirstPull();
      return true;
    } catch (err) {
      Sentry.captureException(err, { tags: { feature: 'ads-chapter', stage: 'link' } });
      notifyLinkFailed();
      return false;
    } finally {
      setIsLinking(false);
    }
  }, [input.merchantCenterId, invalidate, queryClient, startFirstPull]);

  return {
    onConnected: () => { void afterConnect(); },
    // KEIN Dialog waehrend eines laufenden Erst-Abrufs: sein Ergebnis waere
    // derselbe Erst-Abruf ein zweites Mal (Final-Review F3).
    openPicker: () => { if (!busy) setPickerOpen(true); },
    pickerOpen,
    closePicker: () => setPickerOpen(false),
    candidates,
    confirmLink,
    isLinking,
    pollUntil,
    busy,
  };
}
